import React from "react";
import PropTypes from "prop-types";
import { Box, Button, CircularProgress, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", gap: "8px" },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    flexWrap: "wrap",
  },
  heading: { fontSize: "14px", fontWeight: 700, color: "#101828" },
  actions: { display: "flex", gap: "8px", flexWrap: "wrap" },
  touch: { minHeight: "44px" },
  subject: {
    padding: "8px 12px",
    border: "1px solid #d0d5dd",
    borderRadius: "6px",
    backgroundColor: "#fff",
    fontSize: "14px",
  },
  label: { fontSize: "12px", color: "#475467" },
  frame: {
    width: "100%",
    minHeight: "480px",
    border: "1px solid #d0d5dd",
    borderRadius: "6px",
    backgroundColor: "#fff",
  },
  text: {
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    padding: "12px",
    border: "1px solid #d0d5dd",
    borderRadius: "6px",
    backgroundColor: "#fff",
    fontFamily: "monospace",
    margin: 0,
  },
  warnings: {
    margin: 0,
    padding: "8px 12px",
    border: "1px solid #fedf89",
    borderRadius: "6px",
    backgroundColor: "#fffaeb",
    color: "#7a2e0e",
    fontSize: "13px",
  },
  errors: {
    margin: 0,
    padding: "8px 12px 8px 28px",
    border: "1px solid #b42318",
    borderRadius: "6px",
    backgroundColor: "#fef3f2",
    color: "#7a271a",
    fontSize: "13px",
  },
});

/**
 * Rendered preview with sample values. HTML is shown in a sandboxed iframe with scripts
 * disabled, so template content can never run in the admin app.
 */
const PreviewPanel = ({
  preview,
  loading,
  onRefresh,
  onSendTest,
  sending,
  hasSubject,
  unresolvedVariables,
}) => {
  const classes = useStyles();
  const errors = preview?.errors || [];

  return (
    <Box
      className={classes.root}
      component="section"
      aria-labelledby="mt-preview-heading"
    >
      <Box className={classes.header}>
        <Typography
          id="mt-preview-heading"
          component="h2"
          className={classes.heading}
        >
          Preview (sample values)
        </Typography>
        <Box className={classes.actions}>
          <Button
            variant="outlined"
            className={classes.touch}
            onClick={onRefresh}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={18} aria-label="Rendering preview" />
            ) : (
              "Refresh preview"
            )}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            className={classes.touch}
            onClick={onSendTest}
            disabled={sending || errors.length > 0}
          >
            {sending ? "Sending…" : "Send test email to me"}
          </Button>
        </Box>
      </Box>

      <div aria-live="polite">
        {errors.length > 0 && (
          <ul className={classes.errors} role="alert">
            {errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        )}
        {unresolvedVariables.length > 0 && (
          <Box className={classes.warnings}>
            Not supplied by the code yet:{" "}
            {unresolvedVariables.map((tag, i) => (
              <React.Fragment key={tag}>
                {i > 0 && ", "}
                <code>{`{{${tag}}}`}</code>
              </React.Fragment>
            ))}
            . The preview shows them as [[name?]]; real messages leave them
            empty. Saving sends them to developers as requested variables.
          </Box>
        )}
      </div>

      {hasSubject && (
        <Box>
          <Typography className={classes.label}>Subject</Typography>
          <Box className={classes.subject}>
            {preview?.subject || "—  (sender's default subject)"}
          </Box>
        </Box>
      )}

      {preview?.body != null &&
        (preview.html ? (
          <iframe
            title="Rendered message preview"
            className={classes.frame}
            sandbox=""
            srcDoc={preview.body}
          />
        ) : (
          <pre className={classes.text} aria-label="Rendered message preview">
            {preview.body}
          </pre>
        ))}
    </Box>
  );
};

PreviewPanel.propTypes = {
  preview: PropTypes.shape({
    subject: PropTypes.string,
    body: PropTypes.string,
    html: PropTypes.bool,
    errors: PropTypes.arrayOf(PropTypes.string),
  }),
  loading: PropTypes.bool,
  sending: PropTypes.bool,
  onRefresh: PropTypes.func.isRequired,
  onSendTest: PropTypes.func.isRequired,
  hasSubject: PropTypes.bool,
  unresolvedVariables: PropTypes.arrayOf(PropTypes.string),
};

PreviewPanel.defaultProps = {
  preview: null,
  loading: false,
  sending: false,
  hasSubject: true,
  unresolvedVariables: [],
};

export default PreviewPanel;
